import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'
import styled from 'styled-components'

import { Set } from '@blade/std-lib'
import { selectedNetworkAtom } from '@root/entities/network'
import { Provider } from '@root/entities/provider/model'
import {
  finalStretchExploreFiltersAtom,
  graduatedExploreFiltersAtom,
  newExploreFiltersAtom,
} from '@root/features/explore/atoms'
import { exploreFilterDrawer } from '@root/features/explore/atoms/reatom-explore-filter-drawer'

import type { AtomMut } from '@reatom/framework'
import type { ExploreFiltersAtom } from '@root/features/explore/atoms'
import type { ExploreFiltersFormModel } from '@root/features/explore/atoms/reatom-explore-filters-form'
import type { ReatomNumericInputOptional } from '@root/shared/lib/reatom/reatom-numeric-input'

/** The three feeds this board shows, in the order their columns stand. */
const FEEDS: ReadonlyArray<{ label: string; filters: ExploreFiltersAtom }> = [
  { label: 'New pairs', filters: newExploreFiltersAtom },
  { label: 'Final stretch', filters: finalStretchExploreFiltersAtom },
  { label: 'Migrated', filters: graduatedExploreFiltersAtom },
]

type Range = {
  label: string
  unit: string
  min: ReatomNumericInputOptional
  max: ReatomNumericInputOptional
}

/**
 * Pulse V2's own filter panel.
 *
 * The old board's drawer was built for the old board and looks like it, so this
 * is a second face over the same model: the very same per-feed filter atoms, the
 * same draft-then-apply, the same localStorage. Nothing about filtering is
 * reimplemented here — only how it is shown.
 *
 * Every control is wired to a field the backend actually accepts. The design
 * also drew a bonding-percent range, four safety toggles and five named quick
 * presets; none of those exist in the filter contract, so they are not drawn as
 * dead controls. What replaced them in the same slots — transaction count, the
 * per-network social requirements — is real.
 *
 * Design: https://claude.ai/artifact/5DHS184wUvyGV3PGQTbvLS
 */
export const PulseFilters: React.FC = memo(() => {
  const [open] = useAtom(exploreFilterDrawer.exploreFilterDrawer)
  const setOpen = useAction(exploreFilterDrawer.exploreFilterDrawer)
  const close = useAction(exploreFilterDrawer.closeAction)

  if (!open) {
    return undefined
  }

  const active = FEEDS.find((feed) => feed.filters === open)

  return (
    <>
      {/* Click anywhere off the panel to put it away, as the old drawer does. */}
      <Backdrop onClick={() => close()} />
      <Panel aria-label="Filters">
        <div className="fhd">
          <svg
            className="io"
            viewBox="0 0 24 24"
            style={{ width: 13, height: 13, color: 'var(--accent)' }}
          >
            <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
            <circle cx="15" cy="8" r="2.2" />
            <circle cx="9" cy="16" r="2.2" />
          </svg>
          <h2>Filters</h2>
          <span className="mono fwhich">{active?.label}</span>
          <button
            className="ib"
            type="button"
            aria-label="Close"
            style={{ marginLeft: 'auto' }}
            onClick={() => close()}
          >
            <svg
              className="io"
              viewBox="0 0 24 24"
              style={{ width: 12, height: 12 }}
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Each feed keeps its own filters, so the tabs move the panel from one
            set to the next rather than editing a shared one. */}
        <div className="ftabs">
          {FEEDS.map((feed) => (
            <button
              key={feed.label}
              className={feed.filters === open ? 'on' : undefined}
              type="button"
              onClick={() => setOpen(feed.filters)}
            >
              {feed.label}
            </button>
          ))}
        </div>

        <FilterBody key={active?.label} filters={open} />
      </Panel>
    </>
  )
})

const FilterBody: React.FC<{ filters: ExploreFiltersAtom }> = memo(
  ({ filters }) => {
    const form = filters.form

    const size = useMemo<ReadonlyArray<Range>>(
      () => [
        { label: 'Age', unit: 'min', min: form.minAge, max: form.maxAge },
        {
          label: 'Market cap',
          unit: 'usd',
          min: form.minMarketCap,
          max: form.maxMarketCap,
        },
        {
          label: 'Liquidity',
          unit: 'usd',
          min: form.minLiquidityInUsd,
          max: form.maxLiquidityInUsd,
        },
        {
          label: 'Volume',
          unit: 'usd',
          min: form.minVolumeInUsd,
          max: form.maxVolumeInUsd,
        },
        {
          label: 'Holders',
          unit: 'n',
          min: form.minHolders,
          max: form.maxHolders,
        },
        { label: 'Txns', unit: 'n', min: form.minTx, max: form.maxTx },
      ],
      [form],
    )

    const held = useMemo<ReadonlyArray<Range>>(
      () => [
        {
          label: 'Dev',
          unit: '%',
          min: form.minDevHolding,
          max: form.maxDevHolding,
        },
        {
          label: 'Snipers',
          unit: '%',
          min: form.minSnipersHolding,
          max: form.maxSnipersHolding,
        },
        {
          label: 'Top 10',
          unit: '%',
          min: form.minTop10Holding,
          max: form.maxTop10Holding,
        },
        {
          label: 'Insiders',
          unit: '%',
          min: form.minInsidersHolding,
          max: form.maxInsidersHolding,
        },
        {
          label: 'Bundlers',
          unit: '%',
          min: form.minBundlersHolding,
          max: form.maxBundlersHolding,
        },
      ],
      [form],
    )

    return (
      <>
        <div className="fbody">
          <RangeGroup title="Size" rows={size} />
          <RangeGroup title="Supply held" rows={held} />

          <div className="fgrp">
            <div className="fgh">
              <span className="lb">Require</span>
            </div>
            <Toggle label="Has socials" value={form.withAtLeastOneSocial} />
            <Toggle label="On X" value={form.withTwitter} />
            <Toggle label="On Telegram" value={form.withTelegram} />
            <Toggle label="Has a website" value={form.withWebsite} />
          </div>

          <Launchpads form={form} />
        </div>

        <FilterFoot filters={filters} />
      </>
    )
  },
)

/** One titled block of min–max lines, with a count of what is filled in. */
const RangeGroup: React.FC<{
  title: string
  rows: ReadonlyArray<Range>
}> = memo(({ title, rows }) => {
  const [setCount] = useAtom(
    (ctx) =>
      rows.reduce(
        (total, row) =>
          total +
          (ctx.spy(row.min.raw) ? 1 : 0) +
          (ctx.spy(row.max.raw) ? 1 : 0),
        0,
      ),
    [rows],
  )

  return (
    <div className="fgrp">
      <div className="fgh">
        <span className="lb">{title}</span>
        {setCount > 0 && <span className="fn mono">{setCount} set</span>}
      </div>
      {rows.map((row) => (
        <RangeLine key={row.label} row={row} />
      ))}
    </div>
  )
})

/** A filled bound gets a lit border, so a glance down the column shows what is
    actually narrowing the list. */
const RangeLine: React.FC<{ row: Range }> = memo(({ row }) => {
  const [min] = useAtom(row.min.raw)
  const [max] = useAtom(row.max.raw)
  const setMin = useAction(row.min.setRaw)
  const setMax = useAction(row.max.setRaw)

  return (
    <div className="fln">
      <label htmlFor={`f-${row.label}-min`}>{row.label}</label>
      <input
        className={min ? 'fin set' : 'fin'}
        id={`f-${row.label}-min`}
        type="text"
        inputMode="decimal"
        placeholder="min"
        value={min}
        onChange={(event) => setMin(event.target.value)}
      />
      <span className="fdash" />
      <input
        className={max ? 'fin set' : 'fin'}
        type="text"
        inputMode="decimal"
        placeholder="max"
        value={max}
        aria-label={`${row.label} maximum`}
        onChange={(event) => setMax(event.target.value)}
      />
      <span className="funit">{row.unit}</span>
    </div>
  )
})

const Toggle: React.FC<{ label: string; value: AtomMut<boolean> }> = memo(
  ({ label, value }) => {
    const [on, setOn] = useAtom(value)

    return (
      <label className="fsw">
        <span
          className="ftr"
          style={{
            background: on ? 'rgba(245,245,245,.9)' : '#242424',
            justifyContent: on ? 'flex-end' : 'flex-start',
          }}
        >
          <span
            className="fkn"
            style={{ background: on ? 'var(--bg)' : 'var(--t4)' }}
          />
        </span>
        <input
          type="checkbox"
          checked={on}
          onChange={(event) => setOn(event.target.checked)}
        />
        <span style={{ color: on ? 'var(--t1)' : 'var(--t3)' }}>{label}</span>
      </label>
    )
  },
)

/**
 * Which venues count. `providers` holds `'all'` rather than a full set, so
 * turning one off has to expand it first — the same dance the old panel does.
 */
/**
 * The venue table has drifted from the generated `ProviderId`: ids it lists,
 * such as `raydium-launchpad` and `bonk-fun`, are not in the client's union —
 * type errors already stand at `entities/provider/model/provider.ts:70` over
 * exactly this. Until the client is regenerated against the venues we really
 * trade, the id has to be narrowed to reach the filter atom, and it is narrowed
 * here alone rather than at every use.
 */
const asId = (provider: Provider.Provider) => provider.id as Provider.Id

const Launchpads: React.FC<{ form: ExploreFiltersFormModel }> = memo(
  ({ form }) => {
    const [network] = useAtom(selectedNetworkAtom)
    const [selected, setSelected] = useAtom(form.providers)
    const [withMayhem, setWithMayhem] = useAtom(form.withMayhem)

    const providers = Provider.providersByNetwork[network]

    const toggle = (provider: Provider.Provider) => {
      const id = asId(provider)

      if (provider === Provider.pumpFunMayhem) {
        setWithMayhem(!withMayhem)
        return
      }

      if (selected === 'all') {
        setSelected(
          Set.create(providers.filter((it) => it.id !== id).map(asId)),
        )
        return
      }

      const next = Set.toggle(id)(selected)
      setSelected(next.size === providers.length ? 'all' : next)
    }

    return (
      <div className="fgrp">
        <div className="fgh">
          <span className="lb">Launchpads</span>
        </div>
        <div className="fquick">
          {providers.map((provider) => {
            const on =
              provider === Provider.pumpFunMayhem
                ? withMayhem
                : selected === 'all' || selected.has(asId(provider))

            return (
              <button
                key={provider.id}
                className={on ? 'fqc on' : 'fqc'}
                type="button"
                onClick={() => toggle(provider)}
              >
                {provider.title}
              </button>
            )
          })}
        </div>
      </div>
    )
  },
)

/**
 * How many filters are on, then Reset and Apply.
 *
 * The design's "31 of 142 pairs pass right now" is not drawn: no feed response
 * carries a total, and the client only ever holds twenty rows per column, so any
 * denominator here would be made up. The number of filters set is true and is
 * what is shown instead.
 */
const FilterFoot: React.FC<{ filters: ExploreFiltersAtom }> = memo(
  ({ filters }) => {
    const [applied] = useAtom(filters.appliedFiltersCount)
    const [editState] = useAtom(filters.form.filtersEditState)
    const apply = useAction(filters.applyFilters)
    const reset = useAction(filters.resetFilters)
    const close = useAction(exploreFilterDrawer.closeAction)

    return (
      <div className="ffoot">
        <div className="fres">
          <span className="mono">{applied}</span>
          <span className="fresnote">{appliedNote(applied)}</span>
        </div>
        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            className="fbtn"
            type="button"
            style={{
              padding: '0 12px',
              background: 'var(--chip)',
              color: 'var(--t3)',
            }}
            onClick={() => reset()}
          >
            Reset
          </button>
          <button
            className="fbtn"
            type="button"
            disabled={!editState.isChanged || editState.isRangeExceeded}
            style={{
              flexGrow: 1,
              background: 'var(--t1)',
              color: 'var(--bg)',
            }}
            onClick={() => {
              apply()
              close()
            }}
          >
            {editState.isRangeExceeded
              ? 'A minimum is above its maximum'
              : 'Apply'}
          </button>
        </div>
      </div>
    )
  },
)

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
`

/**
 * The panel's own classes are prefixed, because the board already spends `.hd`,
 * `.foot` and `.seg` on its columns and strip. Everything else is the design's
 * measurements unchanged.
 */
const Panel = styled.aside`
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  z-index: 4;
  inline-size: 340px;
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border-inline-start: 1px solid var(--line);

  .fhd {
    height: 38px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px;
    border-bottom: 1px solid var(--line);
  }

  .fhd h2 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.1px;
  }

  .fwhich {
    font-size: 11px;
    color: var(--t4);
  }

  .ftabs {
    display: flex;
    align-items: center;
    gap: 1px;
    padding: 2px;
    margin: 8px 10px 0;
    border-radius: 5px;
    background: var(--line-soft);
  }

  .ftabs button {
    flex-grow: 1;
    height: 22px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
    color: var(--t4);
  }

  .ftabs button.on {
    background: var(--chip-hi);
    color: var(--t1);
  }

  .fbody {
    flex-grow: 1;
    min-height: 0;
    /* The design's body holds still; ours has one more group than it drew, so
       it scrolls rather than squeezing the lines. */
    overflow-y: auto;
    scrollbar-width: none;
    padding: 12px 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .fbody::-webkit-scrollbar {
    display: none;
  }

  .fgrp {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .fgh {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 14px;
  }

  .fn {
    margin-left: auto;
    font-size: 9px;
    font-weight: 500;
    color: var(--t5);
  }

  /* one filter is one 24px line: name, min, dash, max */
  .fln {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 24px;
  }

  .fln label {
    width: 72px;
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 500;
    color: var(--t3);
  }

  .fin {
    flex-grow: 1;
    width: 0;
    height: 24px;
    padding: 0 6px;
    border-radius: 4px;
    border: 1px solid var(--line);
    background: #0d0e12;
    color: var(--t1);
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    outline: none;
  }

  .fin::placeholder {
    color: var(--t5);
  }

  .fin.set {
    border-color: #343850;
    background: #111219;
  }

  .fdash {
    width: 5px;
    height: 1px;
    background: var(--t5);
    flex-shrink: 0;
  }

  .funit {
    width: 26px;
    flex-shrink: 0;
    font-size: 9px;
    font-weight: 500;
    color: var(--t5);
    text-align: right;
  }

  .fsw {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    cursor: pointer;
    font-size: 11px;
    font-weight: 500;
  }

  .fsw input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }

  .ftr {
    flex-shrink: 0;
    width: 24px;
    height: 14px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    padding: 2px;
  }

  .fkn {
    width: 10px;
    height: 10px;
    border-radius: 9999px;
  }

  .fquick {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
  }

  .fqc {
    height: 20px;
    padding: 0 7px;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 500;
    background: var(--chip);
    color: var(--t3);
  }

  .fqc.on {
    background: rgba(82, 111, 255, 0.15);
    color: #9aacff;
  }

  .ffoot {
    flex-shrink: 0;
    padding: 10px;
    border-top: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .fres {
    display: flex;
    align-items: baseline;
    gap: 5px;
  }

  .fres .mono {
    font-size: 15px;
    font-weight: 600;
    color: var(--t1);
  }

  .fresnote {
    font-size: 11px;
    font-weight: 400;
    color: var(--t4);
  }

  .fbtn {
    height: 28px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .fbtn:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

/** Says what the count means, so the number is never left bare. */
const appliedNote = (applied: number) => {
  if (applied === 0) {
    return 'filters — this feed arrives whole'
  }
  if (applied === 1) {
    return 'filter is narrowing this feed'
  }
  return 'filters are narrowing this feed'
}
